import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { HighlightableOption, NavigableOption, SelectableOption } from '../../core';
import { InputBooleanAttribute, isTruthyInput } from '../../core/empty-attribute';
import { TableComponent } from '../table.component';
import { CDS_TABLE_ROW_PARENT } from '../table.interface';

export class RowSelectionChange<T> {
  constructor(
    public source: RowInterface<T>,
    public isUserInput = false
  ) {}
}

export interface RowInterface<T = any> extends HighlightableOption, NavigableOption, SelectableOption {
  selectionChange: EventEmitter<RowSelectionChange<T>>;
  value: T;
  /** `active` is the odd one out: not a status the row is in, but the row whose detail is on screen. */
  state?: 'error' | 'success' | 'warning' | 'deleting' | 'active';
}

@Component({
  standalone: false,
  selector: 'campus-table-v2-row, [table-row]',
  templateUrl: './row.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: CDS_TABLE_ROW_PARENT, useExisting: forwardRef(() => TableComponent) }],
})
export class TableRowComponent<T = any> implements RowInterface<T>, OnChanges {
  private _cd = inject(ChangeDetectorRef);
  protected _parent = inject(CDS_TABLE_ROW_PARENT);
  element = inject(ElementRef).nativeElement;

  //#region Private properties
  private _selected = false;
  //#endregion

  //#region Inputs
  @Input()
  selectable: InputBooleanAttribute;
  @Input()
  value: T;

  @Input() elevateCheckbox: boolean;

  @HostBinding('attr.aria-selected')
  @HostBinding('class.selected')
  @Input()
  get selected(): boolean {
    return this._selected;
  }
  set selected(value: InputBooleanAttribute) {
    this._selected = isTruthyInput(value);
  }
  @Input() state?: RowInterface<T>['state'];
  @Input() loading: boolean;
  //#endregion

  //#region Public properties
  get multiple(): boolean {
    return isTruthyInput(this._parent.multiple);
  }

  get readonly(): boolean {
    return isTruthyInput(this._parent.readonly);
  }

  @Input() disabled: InputBooleanAttribute;

  /**
   * The colour the row's `state` maps to. One binding per class rather than a single
   * `@HostBinding('class')`: that binds the whole class attribute and so loses to the static classes the
   * table puts on the `<tr>`, which is why the colour never reached the DOM.
   */
  @HostBinding('class.error-container')
  public get isError(): boolean {
    return this.state === 'error';
  }

  @HostBinding('class.success-container')
  public get isSuccess(): boolean {
    return this.state === 'success';
  }

  @HostBinding('class.warn-container')
  public get isWarning(): boolean {
    return this.state === 'warning';
  }

  @HostBinding('class.deleting')
  public get isDeleting(): boolean {
    return this.state === 'deleting';
  }

  /**
   * A row is highlighted because it is checkbox-selected, or because it is the one whose detail is on
   * screen — `active`, for tables that highlight without being selectable. Both reasons share one binding:
   * two `class.primary-container` bindings on the same host each write the class every pass, so the second
   * would undo the first.
   */
  @HostBinding('class.primary-container')
  public get isHighlighted(): boolean {
    return this.selected || this.state === 'active';
  }

  //#endregion

  //#region Outputs
  @Output() selectionChange = new EventEmitter<RowSelectionChange<T>>();
  //#endregion

  //#region Lifecycle Hooks
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectable) {
      this.selectable = isTruthyInput(changes.selectable.currentValue);
    }
    if (changes.disabled) {
      this.disabled = isTruthyInput(changes.disabled.currentValue);
    }
  }
  //#endregion

  //#region Public methods

  setActive(): void {
    // Implement setActive logic here
    console.log('Row is set active');
  }

  setInactive(): void {
    // Implement setInactive logic here
    console.log('Row is set inactive');
  }

  select(emitEvent?: boolean): void {
    if (!this._selected) {
      this._selected = true;
      this._cd.markForCheck();

      if (emitEvent) {
        this._emitSelectionChangeEvent();
      }
    }
  }

  deselect(emitEvent?: boolean): void {
    if (this._selected) {
      this._selected = false;
      this._cd.markForCheck();
    }

    if (emitEvent) {
      this._emitSelectionChangeEvent();
    }
  }

  selectViaInteraction(): void {
    if (!this.disabled) {
      this._selected = this.multiple ? !this._selected : true;
      this._cd.markForCheck();
      this._emitSelectionChangeEvent(true);
    }
  }

  onCheck(checked: boolean) {
    if (!this.disabled) {
      if (checked) {
        this.select(true);
      } else {
        this.deselect(true);
      }
    }
  }

  //#endregion

  //#region Private methods
  private _emitSelectionChangeEvent(isUserInput = false): void {
    this.selectionChange.emit(new RowSelectionChange(this, isUserInput));
  }
  //#endregion
}
